import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { type Shortcuts, tooltip } from '@databases/keyboard-shortcuts';
import { useKeyboardShortcut } from '@databases/react';
import { type ActionType, type SelectionMode } from '@databases/types/database';
import React, { type ReactNode, useState } from 'react';
import { useSnapshot } from 'valtio';

import { useMediaQuery } from '@canvas/hooks';

import { actionBarState } from './action-bar-state';
import { useActionBarAnalytics } from './hooks/useActionBarAnalytics';

export type ActionComponentProps = {
  type: ActionType;
  message: string;
  isPopupAction?: boolean;
  isTriggerAction?: boolean;
  selectionMode?: SelectionMode;
  onClick: () => void;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  isSelected?: boolean;
  shortcut?: Shortcuts;
  handleMouseEnter?: () => void;
  handleMouseLeave?: () => void;
};

const getPressableContentStyles = (isPopupAction: boolean) =>
  xcss({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    flex: '1',
    paddingInline: 'space.150',
    gap: 'space.075',
    minHeight: isPopupAction ? '32px' : '24px',
    paddingBlock: isPopupAction ? 'space.100' : 'space.0',
    font: token('font.body', '14px'),
  });

const getPressableStyles = (isPopupAction: boolean) =>
  xcss({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    cursor: 'pointer',
    width: '100%',
    border: `${token('border.width', '1px')} solid transparent`,
    background: token('color.background.neutral.bold', '#292A2E'),
    flex: '1',
    whiteSpace: 'nowrap',
    borderRadius: token('radius.small', '4px'),
    ':hover': {
      background: token('color.background.neutral.bold.hovered'),
      ...(isPopupAction && { borderRadius: '0' }),
    },
  });

const getShortcutStyles = (isHovered: boolean) =>
  xcss({
    marginLeft: 'auto',
    borderRadius: 'radius.small',
    paddingInline: 'space.050',
    ...(isHovered && { backgroundColor: 'color.background.neutral' }),
  });

const selectedStateStyles = xcss({
  borderRadius: token('radius.small', '4px'),
  border: `${token('border.width')} solid ${token(
    'color.background.accent.blue.subtle'
  )}`,
  background: token('color.background.accent.blue.bolder.pressed'),
});

export const ActionComponent = ({
  type,
  isTriggerAction = false,
  isPopupAction = false,
  iconBefore,
  iconAfter,
  message,
  selectionMode = 'entry',
  onClick,
  isSelected = false,
  shortcut,
  handleMouseEnter = () => {},
  handleMouseLeave = () => {},
}: ActionComponentProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onSelectActionItem } = useActionBarAnalytics();
  const { entryId, totalSelectedCount, contentId } = useSnapshot(actionBarState.state);
  const isSingleSelectedEntry = selectionMode === 'entry' && totalSelectedCount === 1;

  const handleClick = () => {
    if (!isTriggerAction) {
      onSelectActionItem({
        type,
        contentId,
        totalSelectedCount,
        selectionMode,
        isSingleSelectedEntry,
        entryId,
      });
    }
    onClick();
  };

  const renderTextColor = () => {
    if (type === 'delete' || type === 'delete-entry' || type === 'delete-field') {
      return token('color.background.danger.pressed');
    } else if (
      isSelected &&
      (type === 'move' || type === 'edit-values' || type === 'add')
    ) {
      return token('color.background.accent.blue.subtler.hovered');
    } else {
      return token('color.text.inverse');
    }
  };
  const isCompact = useMediaQuery('(max-width: 1000px)');
  useKeyboardShortcut(isPopupAction ? undefined : shortcut?.keyboardShortCut, onClick);
  const shortcutText = shortcut ? tooltip(shortcut.keyMap) : '';

  const actionContent = (
    <Pressable
      testId={`${type}-${selectionMode}-action`}
      padding="space.0"
      xcss={[getPressableStyles(isPopupAction), isSelected && selectedStateStyles]}
      style={{
        color: renderTextColor(),
      }}
      onClick={handleClick}
      onMouseEnter={() => {
        handleMouseEnter();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        handleMouseLeave();
        setIsHovered(false);
      }}
    >
      <Box xcss={getPressableContentStyles(isPopupAction)}>
        {iconBefore}
        {(!isCompact || isPopupAction) && message}
        {isPopupAction && shortcutText && (
          <Box xcss={getShortcutStyles(isHovered)}>{shortcutText}</Box>
        )}
        {iconAfter}
      </Box>
    </Pressable>
  );

  if (!isPopupAction && (shortcutText || isCompact)) {
    const tooltipContent = isCompact ? (
      <>
        {message} {shortcutText}
      </>
    ) : (
      shortcutText
    );
    return (
      <ToolTip content={tooltipContent} position="top">
        {actionContent}
      </ToolTip>
    );
  }

  return actionContent;
};
